import React from 'react';

/**
 * CustomerResponseSection - Komponent för kund att svara på offert
 * 
 * Visar tre alternativ:
 * - Godkänn offert
 * - Kommentera (döljs om feedback-konversation har startats)
 * - Neka offert
 * 
 * Om Kommentera eller Neka väljs, visas ett obligatoriskt kommentarsfält.
 */
const CustomerResponseSection = ({
  response,
  comment,
  submitting,
  feedbackThread,
  onResponseChange,
  onCommentChange,
  onSubmit,
  isNewerVersion = false,
  // Spåret. Ett tillägg godkänns VID SIDAN AV den redan godkända offerten, så
  // texterna om att boka byggaren och starta projekteringen gäller inte — det
  // är redan gjort och kunden är i produktion.
  tillaggsNummer = null
}) => {
  // Kontrollera om det finns feedback-meddelanden från kunden
  const hasFeedbackConversation = feedbackThread?.messages?.some(m => m.by === 'customer');

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-leadme-pink to-leadme-pink-dark text-white p-6 rounded-t-lg">
        <h2 className="text-lg font-semibold">
          {tillaggsNummer != null ? `Svara på tilläggsoffert ${tillaggsNummer}` : 'Svara på offert'}
        </h2>
        <p className="text-sm opacity-90 mt-1">
          {hasFeedbackConversation 
            ? 'Välj ditt slutgiltiga svar på offerten' 
            : 'Vänligen välj hur du vill svara på denna offert'
          }
        </p>
      </div>

      <div className="p-6">
        {/* Info-meddelande om feedback-konversation har startats */}
        {hasFeedbackConversation && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💬 Du har påbörjat en konversation via feedback-sektionen. Använd feedback-sektionen för fortsatta diskussioner och välj nedan när du är redo för ett slutgiltigt svar.
            </p>
          </div>
        )}
        
        {/* Val av svar */}
        <div className="space-y-4 mb-6">
          {/* Godkänn offert / Boka byggare */}
          <ResponseOption
            type="accepted"
            selected={response === 'accepted'}
            onSelect={() => onResponseChange('accepted')}
            title={tillaggsNummer != null
              ? "Godkänn tilläggsoffert"
              : (isNewerVersion ? "Godkänn offert" : "Boka byggare")}
            description={tillaggsNummer != null
              ? "Jag godkänner det tillkommande arbetet enligt specifikationen ovan, till angivet pris. Arbetet utförs av samma byggare och läggs till i det pågående projektet. Min ursprungliga offert och mitt avtal gäller fortsatt."
              : (isNewerVersion
                ? "Jag godkänner den uppdaterade offerten."
                : "Jag vill gå vidare med denna byggare. Detta innebär att vi startar projektering, bokar in projektet i kalendern och påbörjar planeringen."
              )
            }
            colorClass="green"
          />

          {/* Kommentera - visa ENDAST om ingen feedback-konversation har startats */}
          {!hasFeedbackConversation && (
            <ResponseOption
              type="commented"
              selected={response === 'commented'}
              onSelect={() => onResponseChange('commented')}
              title="Kommentera"
              description="Jag har frågor eller kommentarer kring offerten."
              colorClass="blue"
            />
          )}

          {/* Neka */}
          <ResponseOption
            type="declined"
            selected={response === 'declined'}
            onSelect={() => onResponseChange('declined')}
            title={tillaggsNummer != null ? 'Neka tilläggsoffert' : 'Neka offert'}
            description={tillaggsNummer != null
              ? 'Jag vill inte beställa det tillkommande arbetet. Det pågående projektet fortsätter som planerat.'
              : 'Jag vill inte gå vidare med denna offert.'}
            colorClass="red"
          />
        </div>

        {/* Kommentarsfält - visa om "Kommentera" eller "Neka" är valt */}
        {(response === 'commented' || response === 'declined') && (
          <div className="mb-6">
            <label htmlFor="offert-comment" className="block text-sm font-medium text-gray-700 mb-2">
              {response === 'declined' ? 'Varför nekar du offerten? *' : 'Din kommentar *'}
            </label>
            <textarea
              id="offert-comment"
              rows={5}
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              placeholder={
                response === 'declined'
                  ? 'Vänligen förklara varför du nekar offerten...'
                  : 'Beskriv dina frågor eller kommentarer...'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">* Obligatoriskt fält</p>
          </div>
        )}

        {/* Skicka-knapp */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!response || submitting}
            className="px-6 py-3 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2 font-medium"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Skickar...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                <span>Skicka svar</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * ResponseOption - Intern komponent för att visa ett svarsalternativ
 */
const ResponseOption = ({ type, selected, onSelect, title, description, colorClass }) => {
  const colorClasses = {
    green: {
      border: selected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300 hover:bg-green-50',
      checkbox: selected ? 'border-green-500 bg-green-500' : 'border-gray-300'
    },
    blue: {
      border: selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50',
      checkbox: selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
    },
    red: {
      border: selected ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300 hover:bg-red-50',
      checkbox: selected ? 'border-red-500 bg-red-500' : 'border-gray-300'
    }
  };

  const colors = colorClasses[colorClass];

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full p-4 border-2 rounded-lg text-left transition-all ${colors.border}`}
    >
      <div className="flex items-start">
        <div
          className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 ${colors.checkbox}`}
        >
          {selected && (
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <div className="ml-4">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );
};

export default CustomerResponseSection;

